/**
 * Blog Grid - AJAX Filters & Pagination
 * Handles dynamic filtering and pagination for blog pages
 * Shares similar structure with collection-grid.js
 */

class BlogGrid {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.blogHandle = container.dataset.blogHandle;
    this.articlesPerPage = parseInt(container.dataset.articlesPerPage) || 12;

    // Elements
    this.articlesGrid = container.querySelector('[data-articles-grid]');
    this.pagination = container.querySelector('[data-pagination]');
    this.filtersToolbar = container.querySelector('[data-filters-toolbar]');
    this.clearFiltersBtn = container.querySelector('[data-filter-clear]');

    // State
    this.currentTag = this.getCurrentTag();
    this.currentSort = 'date-desc';
    this.isLoading = false;

    this.init();
  }

  init() {
    this.bindFilterEvents();
    this.bindPaginationEvents();
    this.initDropdowns();
  }

  getCurrentTag() {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/');
    const taggedIndex = pathParts.indexOf('tagged');
    if (taggedIndex !== -1 && pathParts[taggedIndex + 1]) {
      return pathParts[taggedIndex + 1];
    }
    return '';
  }

  // =========================================================================
  // Dropdowns
  // =========================================================================

  initDropdowns() {
    const toggleButtons = this.container.querySelectorAll('[data-filter-toggle]');

    toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const wrapper = button.closest('[data-filter-wrapper]');
        const dropdown = wrapper?.querySelector('[data-filter-dropdown]');

        // Close other dropdowns
        this.closeAllDropdowns(dropdown);

        if (dropdown) {
          const isOpen = dropdown.classList.contains('is-open');
          dropdown.classList.toggle('is-open', !isOpen);
          button.setAttribute('aria-expanded', !isOpen);
        }
      });
    });

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-filter-wrapper]')) {
        this.closeAllDropdowns();
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
  }

  closeAllDropdowns(except = null) {
    const dropdowns = this.container.querySelectorAll('[data-filter-dropdown]');
    dropdowns.forEach(dropdown => {
      if (dropdown !== except) {
        dropdown.classList.remove('is-open');
        const button = dropdown.closest('[data-filter-wrapper]')?.querySelector('[data-filter-toggle]');
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  // =========================================================================
  // Filter Events
  // =========================================================================

  bindFilterEvents() {
    // Tag inputs (radio)
    const tagInputs = this.container.querySelectorAll('[data-tag-input]');
    tagInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.handleTagChange(input.value);
      });
    });

    // Sort inputs (radio)
    const sortInputs = this.container.querySelectorAll('[data-sort-input]');
    sortInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.handleSortChange(input.value);
      });
    });

    // Clear filters
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  handleTagChange(tag) {
    this.currentTag = tag;
    this.fetchArticles(1);
    this.updateClearButton();
    this.closeAllDropdowns();
  }

  handleSortChange(value) {
    this.currentSort = value;
    this.fetchArticles(1);
    this.closeAllDropdowns();
  }

  clearAllFilters() {
    // Reset tag to "all"
    const allTagInput = this.container.querySelector('[data-tag-input][value=""]');
    if (allTagInput) {
      allTagInput.checked = true;
    }

    // Reset sort to default
    const defaultSort = this.container.querySelector('[data-sort-input][value="date-desc"]');
    if (defaultSort) {
      defaultSort.checked = true;
    }

    this.currentTag = '';
    this.currentSort = 'date-desc';
    this.fetchArticles(1);
    this.updateClearButton();
  }

  updateClearButton() {
    if (!this.clearFiltersBtn) return;
    this.clearFiltersBtn.style.display = this.currentTag ? 'block' : 'none';
  }

  // =========================================================================
  // Pagination Events
  // =========================================================================

  bindPaginationEvents() {
    if (!this.pagination) return;

    this.pagination.addEventListener('click', (e) => {
      const button = e.target.closest('[data-page]');
      if (!button || button.disabled) return;

      const url = new URL(button.dataset.page, window.location.origin);
      const page = url.searchParams.get('page') || 1;

      this.fetchArticles(page);
    });
  }

  // =========================================================================
  // Fetch Articles (AJAX)
  // =========================================================================

  async fetchArticles(page = 1) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.container.classList.add('is-loading');

    // Build URL
    let basePath = `/blogs/${this.blogHandle}`;
    if (this.currentTag) {
      basePath += `/tagged/${this.currentTag}`;
    }

    const params = new URLSearchParams();
    params.set('page', page);
    params.set('section_id', this.sectionId);

    const url = `${basePath}?${params.toString()}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Fetch failed');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update articles grid
      const newGrid = doc.querySelector('[data-articles-grid]');
      if (newGrid && this.articlesGrid) {
        this.articlesGrid.innerHTML = newGrid.innerHTML;
      }

      // Update pagination
      const newPagination = doc.querySelector('[data-pagination]');
      if (this.pagination) {
        if (newPagination) {
          this.pagination.innerHTML = newPagination.innerHTML;
          this.pagination.style.display = '';
        } else {
          this.pagination.style.display = 'none';
        }
      }

      // Update URL without reload
      const newUrl = this.currentTag
        ? `/blogs/${this.blogHandle}/tagged/${this.currentTag}${page > 1 ? `?page=${page}` : ''}`
        : `/blogs/${this.blogHandle}${page > 1 ? `?page=${page}` : ''}`;
      window.history.pushState({}, '', newUrl);

      // Sort articles client-side (since Shopify doesn't support blog sorting)
      this.sortArticles();

      // Scroll to top of grid
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      this.isLoading = false;
      this.container.classList.remove('is-loading');
    }
  }

  // =========================================================================
  // Client-side Sorting (Shopify doesn't support blog sorting via URL)
  // =========================================================================

  sortArticles() {
    const articles = Array.from(this.articlesGrid.querySelectorAll('.blog-article-card'));
    if (articles.length === 0) return;

    articles.sort((a, b) => {
      const dateA = new Date(a.querySelector('[datetime]')?.getAttribute('datetime') || 0);
      const dateB = new Date(b.querySelector('[datetime]')?.getAttribute('datetime') || 0);
      const titleA = a.querySelector('.blog-article-title')?.textContent?.trim() || '';
      const titleB = b.querySelector('.blog-article-title')?.textContent?.trim() || '';

      switch (this.currentSort) {
        case 'date-asc':
          return dateA - dateB;
        case 'date-desc':
          return dateB - dateA;
        case 'title-asc':
          return titleA.localeCompare(titleB, 'pl');
        case 'title-desc':
          return titleB.localeCompare(titleA, 'pl');
        default:
          return dateB - dateA;
      }
    });

    // Re-append sorted articles
    articles.forEach(article => {
      this.articlesGrid.appendChild(article);
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.blog-grid-section');
  containers.forEach(container => {
    new BlogGrid(container);
  });
});

// Reinitialize on section reload (Shopify editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.blog-grid-section');
    if (container) {
      new BlogGrid(container);
    }
  });
}
